import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import { getApiUrl } from '../config/apiConfig';
import clientPrinterService from '../services/clientPrinterService';
import './Pedidos.css';
import './Mesas.css'; // btn-cerrar styles

const PedidoDirectoComponent = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { caja, cajasAbiertas, cajaActiva } = useCaja();

  // Constante para la mesa de pedidos directos
  const MESA_DIRECTA_ID = 910100;

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('todas');
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [nuevoPedido, setNuevoPedido] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mensajeAccion, setMensajeAccion] = useState('');
  const [creandoDirecto, setCreandoDirecto] = useState(false);

  // Caja seleccionada para este pedido directo
  // Por defecto: la caja activa del contexto
  const [idCajaSeleccionada, setIdCajaSeleccionada] = useState(null);

  // Caja asignada al usuario obtenida en tiempo real (evita JWT desactualizado)
  const [cajaAsignadaDB, setCajaAsignadaDB] = useState(auth?.user?.id_caja_asignada || null);
  const [barraAsignadaDB, setBarraAsignadaDB] = useState(auth?.user?.id_barra_asignada || null);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);

  // Fetch real-time de asignaciones para evitar JWT desactualizado
  useEffect(() => {
    if (!auth?.token || !['MOZO', 'CAJERO'].includes(auth?.user?.rol)) return;
    fetch(getApiUrl('/usuarios-admin/mis-asignaciones'), {
      headers: { 'Authorization': `Bearer ${auth.token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { 
        if (data) {
          setCajaAsignadaDB(data.id_caja_asignada || null);
        } 
      })
      .catch(() => {});
  }, [auth?.token, auth?.user?.rol]);

  // Inicializar la caja seleccionada: prioridad → caja asignada al MOZO → caja activa del contexto
  useEffect(() => {
    if (auth?.user?.rol === 'MOZO' && cajaAsignadaDB) {
      const cajaAsig = cajasAbiertas.find(c => c.id === cajaAsignadaDB);
      if (cajaAsig) { setIdCajaSeleccionada(cajaAsig.id); return; }
    }
    if (cajaActiva?.id && !idCajaSeleccionada) {
      setIdCajaSeleccionada(cajaActiva.id);
    }
  }, [cajaActiva, cajaAsignadaDB, cajasAbiertas]);

  useEffect(() => {
    const fetchInicial = async () => {
      try {
        setLoading(true);

        // Cargar productos
        const responseProductos = await fetch(getApiUrl('/productos'), {
          headers: { 'Authorization': `Bearer ${auth?.token}` }
        });
        if (!responseProductos.ok) throw new Error('Error al cargar productos');
        const dataProductos = await responseProductos.json();
        const productosNormalizados = dataProductos.map(p => ({
          ...p,
          precio: typeof p.precio === 'string' ? parseFloat(p.precio) : p.precio,
          costo: typeof p.costo === 'string' ? parseFloat(p.costo) : p.costo
        }));
        setProductos(productosNormalizados);
        const categoriasUnicas = [...new Set(dataProductos.map(p => p.categoria))];
        setCategorias(categoriasUnicas);



        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchInicial();
  }, [auth?.token]);

  const filtrarProductos = () => {
    let resultado = productos;
    if (categoriaSeleccionada !== 'todas') {
      resultado = resultado.filter(p => p.categoria === categoriaSeleccionada);
    }
    if (busquedaProducto.trim()) {
      resultado = resultado.filter(p =>
        p.nombre.toLowerCase().includes(busquedaProducto.toLowerCase())
      );
    }
    return resultado;
  };

  const agregarProductoAPedido = (producto) => {
    const productoExistente = nuevoPedido.find(item => item.id === producto.id);

    if (productoExistente) {
      setNuevoPedido(nuevoPedido.map(item =>
        item.id === producto.id ? { ...item, cantidad: item.cantidad + 1 } : item
      ));
    } else {
      setNuevoPedido([...nuevoPedido, { ...producto, cantidad: 1 }]);
    }
  };

  const quitarProductoDePedido = (productoId) => {
    setNuevoPedido(nuevoPedido.filter(item => item.id !== productoId));
  };

  const actualizarCantidadProducto = (productoId, nuevaCantidad) => {
    if (nuevaCantidad <= 0) {
      quitarProductoDePedido(productoId);
      return;
    }

    setNuevoPedido(nuevoPedido.map(item =>
      item.id === productoId ? { ...item, cantidad: nuevaCantidad } : item
    ));
  };

  const calcularTotal = () => {
    return nuevoPedido.reduce((total, item) => total + (item.precio * item.cantidad), 0).toFixed(2);
  };
  const mostrarVentanaConfirmacion = () => {
    if (nuevoPedido.length === 0) {
      setError('No hay productos en el pedido');
      return;
    }
    setMostrarConfirmacion(true);
  };

  // Actualizar el estado del pedido principal y todos sus detalles
  const actualizarEstadoPedidoYDetalles = async (pedidoId, detallesIds) => {
    const estado = 'en_espera';
    try {
      if (detallesIds && detallesIds.length > 0) {
        const actualizacionesDetalles = detallesIds.map(detalleId =>
          fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${detalleId}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${auth?.token}`
            },
            body: JSON.stringify({ estado })
          })
        );
        await Promise.all(actualizacionesDetalles);
      }

      const responsePedido = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({ estado })
      });

      if (!responsePedido.ok) {
        throw new Error('Error al actualizar el estado del pedido');
      }

      return true;
    } catch (error) {
      console.error('Error al actualizar el estado del pedido:', error);
      throw error;
    }
  };

  const crearPedidoDirecto = async () => {
    try {
      setCreandoDirecto(true);
      setMostrarConfirmacion(false);

      const productosData = nuevoPedido.map(item => ({
        id_producto: item.id,
        cantidad: item.cantidad,
        notas: ''
      }));

      const pedidoData = {
        id_mesa: MESA_DIRECTA_ID,
        productos: productosData,
        observaciones: observaciones,
        es_pedido_directo: true,
        // Enviar la caja seleccionada para este pedido directo
        id_caja: idCajaSeleccionada || cajaActiva?.id || null,
      };

      const response = await fetch(getApiUrl('/pedidos'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify(pedidoData)
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Error response:', text);
        throw new Error(`Error ${response.status}: ${text.substring(0, 100)}...`);
      }

      const responseData = await response.json();
      console.log('Respuesta del servidor:', responseData);

      setMensajeAccion('¡Pedido directo creado con éxito!');

      const pedidoId = responseData.id;

      // Auto-imprimir comanda en cliente con Motor de Impresión Cliente Universal
      try {
        const mesaObj = responseData?.mesa || responseData;
        const pisoMesa = mesaObj?.piso ?? mesaObj?.numero_piso ?? mesaObj?.piso_mesa ?? auth?.user?.piso_asignado ?? 1;
        const mesaNumero = mesaObj?.numero ?? mesaObj?.numero_mesa;
        const mesaFormateada = mesaNumero 
          ? (String(mesaNumero).toLowerCase().startsWith('mesa') ? String(mesaNumero) : `Mesa ${mesaNumero}`)
          : 'DIRECTO';

        const datosComanda = {
          ordenId: pedidoId,
          id: pedidoId,
          mesa: mesaFormateada,
          piso: pisoMesa,
          mozo: auth?.user?.nombre || auth?.user?.usuario || 'Mozo',
          fecha: new Date().toLocaleDateString('es-PE'),
          hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
          items: nuevoPedido.map(item => ({
            nombre: item.nombre,
            cantidad: item.cantidad,
            notas: item.notas || '',
            categoria: item.categoria_nombre || item.categoria || item.nombre_categoria || item.tiempo || 'GENERAL'
          })),
          notaGeneral: observaciones || ''
        };

        await clientPrinterService.imprimirComandaCliente(datosComanda, 'NUEVA');
      } catch (printErr) {
        console.error('Error al enviar comanda directa a la impresora:', printErr);
      }

      const detallesResponse = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });

      if (!detallesResponse.ok) {
        throw new Error('Error al obtener detalles del pedido');
      }

      const pedidoCompleto = await detallesResponse.json();
      const detallesIds = pedidoCompleto.detalles.map(detalle => detalle.id);

      await actualizarEstadoPedidoYDetalles(pedidoId, detallesIds);

      if (auth?.user?.rol === 'MOZO' || auth?.role === 'MOZO') {
        navigate('/pedidos-directos');
      } else {
        navigate(`/pago/${pedidoId}`);
      }

    } catch (err) {
      console.error('Error completo:', err);
      setError(err.message);
    } finally {
      setCreandoDirecto(false);
    }
  };

  const cancelarCreacion = () => {
    setMostrarConfirmacion(false);
  };

  if (loading) {
    return <div className="loading">Cargando productos...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">Error: {error}</div>
        <button
          onClick={() => setError(null)}
          className="btn-volver"
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  if (caja.loading) return <div className="loading">Verificando estado de caja...</div>;

  if (!caja.estaAbierta) return (
    <div className="caja-cerrada-aviso">
      <h3>⚠️ Caja no aperturada</h3>
      <p>No se pueden crear pedidos directos sin abrir la caja primero.</p>
      <button className="btn-ir-caja" onClick={() => navigate('/caja')}>Ir a Caja</button>
    </div>
  );

  return (
    <div className="pedidos-container">
      <div className="pedidos-header">
        <h2>Pedido Directo</h2>
        <div className="pedido-directo-info">
          <p>Los pedidos directos son para llevar o consumir en el momento sin asignación de mesa</p>
        </div>
        <button
        className="btn-volver"
          onClick={() => navigate(-1)}
          title="Cancelar y volver"
        >
          ✕ Cancelar
        </button>
      </div>

      {mensajeAccion && (
        <div className="mensaje-confirmacion">
          {mensajeAccion}
        </div>
      )}

      <div className="nuevo-pedido-grid">
        <div className="productos-section">
          <div className="buscador-productos">
            <input
              type="text"
              placeholder="Buscar producto..."
              value={busquedaProducto}
              onChange={(e) => setBusquedaProducto(e.target.value)}
              className="input-busqueda-producto"
            />
            {busquedaProducto && (
              <button className="btn-limpiar-busqueda" onClick={() => setBusquedaProducto('')}>×</button>
            )}
          </div>
          <div className="categorias-tabs">
            <button
              className={categoriaSeleccionada === 'todas' ? 'active' : ''}
              onClick={() => setCategoriaSeleccionada('todas')}
            >
              Todas
            </button>
            {categorias.map(categoria => (
              <button
                key={categoria}
                className={categoriaSeleccionada === categoria ? 'active' : ''}
                onClick={() => setCategoriaSeleccionada(categoria)}
              >
                {categoria}
              </button>
            ))}
          </div>

          <div className="productos-grid">
            {filtrarProductos().map(producto => (
              <div
                key={producto.id}
                className="producto-card"
                onClick={() => agregarProductoAPedido(producto)}
              >
                <div className="producto-nombre">{producto.nombre}</div>
                <div className="producto-precio">S/. {producto.precio.toFixed(2)}</div>
                <div className="producto-stock">Stock: {producto.stock_actual} {producto.unidad_medida}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="resumen-pedido">
          <h3>Resumen del Pedido Directo</h3>

          {nuevoPedido.length === 0 ? (
            <div className="pedido-vacio">
              <p>Selecciona productos para agregar al pedido</p>
            </div>
          ) : (
            <>
              <div className="items-pedido">
                {nuevoPedido.map(item => (
                  <div key={item.id} className="item-pedido">
                    <div className="item-info">
                      <div className="item-nombre">{item.nombre}</div>
                      <div className="item-precio">S/. {item.precio.toFixed(2)}</div>
                    </div>
                    <div className="item-controles">
                      <button
                        className="btn-cantidad"
                        onClick={() => actualizarCantidadProducto(item.id, item.cantidad - 1)}
                      >
                        -
                      </button>
                      <span className="item-cantidad">{item.cantidad}</span>
                      <button
                        className="btn-cantidad"
                        onClick={() => actualizarCantidadProducto(item.id, item.cantidad + 1)}
                      >
                        +
                      </button>
                      <button
                        className="btn-eliminar"
                        onClick={() => quitarProductoDePedido(item.id)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="item-subtotal">
                      S/. {(item.precio * item.cantidad).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="observaciones">
                <label htmlFor="observaciones">Observaciones del pedido:</label>
                <textarea
                  id="observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Agregar indicaciones especiales..."
                  rows={3}
                />
              </div>

              <div className="total-pedido">
                <div className="label">Total a Pagar:</div>
                <div className="monto">S/. {calcularTotal()}</div>
              </div>

              <button
                className="btn-crear-pedido"
                onClick={mostrarVentanaConfirmacion}
                disabled={creandoDirecto || nuevoPedido.length === 0}
              >
                {creandoDirecto ? '⏳ Imprimiendo comanda...' : (auth?.user?.rol === 'MOZO' || auth?.role === 'MOZO' ? 'Crear Pedido Directo' : 'Crear Pedido y Proceder al Pago')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal de confirmación */}
      {mostrarConfirmacion && (
        <div className="modal-confirmacion">
          <div className="modal-content">
            <h3>Confirmar Pedido Directo</h3>
            <p>¿Deseas crear este pedido directo por S/. {calcularTotal()}?</p>

            {/* Caja: CAJERO → info de cajaActiva, MOZO asignado → info, varios → selector, uno → info */}
            {auth?.user?.rol === 'CAJERO' ? (
              <p style={{ fontSize: '0.9rem', color: '#666', margin: '8px 0' }}>
                Caja: <strong>{cajaActiva?.nombre || 'Caja asignada'}</strong>
              </p>
            ) : auth?.user?.rol === 'MOZO' && cajaAsignadaDB ? (
              <p style={{ fontSize: '0.9rem', color: '#666', margin: '8px 0' }}>
                Caja asociada: <strong>{cajasAbiertas.find(c => c.id === cajaAsignadaDB)?.nombre || `Caja #${cajaAsignadaDB}`}</strong>
              </p>
            ) : cajasAbiertas.length > 1 ? (
              <div className="form-group" style={{ margin: '12px 0' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                  ¿A qué caja se dirige este pedido?
                </label>
                <select
                  value={idCajaSeleccionada || ''}
                  onChange={(e) => setIdCajaSeleccionada(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }}
                >
                  {cajasAbiertas.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </div>
            ) : cajasAbiertas.length === 1 ? (
              <p style={{ fontSize: '0.9rem', color: '#666', margin: '8px 0' }}>
                Caja: <strong>{cajasAbiertas[0]?.nombre}</strong>
              </p>
            ) : null}
            

            <p>{auth?.user?.rol === 'MOZO' || auth?.role === 'MOZO' ? 'Una vez creado, serás redirigido a la lista de pedidos directos.' : 'Una vez creado, serás redirigido automáticamente a la pantalla de pago.'}</p>
            <div className="modal-buttons">
              <button onClick={cancelarCreacion} className="btn-cancelar-accion" disabled={creandoDirecto}>
                Cancelar
              </button>
              <button
                onClick={crearPedidoDirecto}
                className="btn-confirmar"
                disabled={creandoDirecto}
              >
                {creandoDirecto ? '⏳ Imprimiendo comanda...' : (auth?.user?.rol === 'MOZO' || auth?.role === 'MOZO' ? 'Confirmar Pedido' : 'Confirmar y Pagar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PedidoDirectoComponent;
